// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  components/EnviarWhatsAppModal.tsx
//  Modal reutilizável para envio de mensagem WhatsApp (texto livre ou template
//  com placeholders). Use em qualquer tela que precise disparar mensagem.
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Send, MessageSquare } from 'lucide-react';

import { Button }   from '@/components/ui/button';
import { Input }    from '@/components/ui/input';
import { Label }    from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import {
  useEnviarWhatsApp, useWhatsAppTemplates,
} from '@/features/whatsapp/hooks/useWhatsApp';
import { getApiErrorMessage } from '@/lib/apiClient';

interface EnviarWhatsAppModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Número pré-preenchido (aceita máscara). */
  numeroInicial?: string;
  /** ID da pessoa — vai para o log de histórico. */
  pessoaId?: number;
  /** Variáveis pré-definidas para templates (ex: {"nome": "João"}). */
  variaveisIniciais?: Record<string, string>;
}

const PLACEHOLDER_REGEX = /\{(\w+)\}/g;

export function EnviarWhatsAppModal({
  open, onOpenChange,
  numeroInicial = '',
  pessoaId,
  variaveisIniciais = {},
}: EnviarWhatsAppModalProps) {
  const [modo, setModo]           = useState<'texto' | 'template'>('texto');
  const [numero, setNumero]       = useState('');
  const [mensagem, setMensagem]   = useState('');
  const [templateChave, setTemplateChave] = useState<string>('');
  const [variaveis, setVariaveis] = useState<Record<string, string>>({});

  const templates = useWhatsAppTemplates();
  const enviar    = useEnviarWhatsApp();

  // Reset ao abrir
  useEffect(() => {
    if (open) {
      setNumero(numeroInicial);
      setMensagem('');
      setTemplateChave('');
      setVariaveis(variaveisIniciais);
      setModo('texto');
    }
  }, [open, numeroInicial, variaveisIniciais]);

  const templateSelecionado = templates.query.data?.find(t => t.chave === templateChave);

  // Extrai placeholders únicos do corpo do template
  const placeholdersTemplate = useMemo(() => {
    if (!templateSelecionado) return [] as string[];
    const matches = Array.from(templateSelecionado.corpo.matchAll(PLACEHOLDER_REGEX));
    return Array.from(new Set(matches.map(m => m[1])));
  }, [templateSelecionado]);

  // Preview do template com placeholders aplicados
  const preview = useMemo(() => {
    if (!templateSelecionado) return '';
    return templateSelecionado.corpo.replace(PLACEHOLDER_REGEX, (_, key: string) =>
      variaveis[key] ?? `{${key}}`);
  }, [templateSelecionado, variaveis]);

  const handleEnviar = async () => {
    try {
      const r = await enviar.mutateAsync({
        numero,
        mensagem:      modo === 'texto' ? mensagem : undefined,
        templateChave: modo === 'template' ? templateChave : undefined,
        variaveis:     modo === 'template' ? variaveis : undefined,
        pessoaId,
      });

      if (r.sucesso) {
        toast.success('Mensagem enviada com sucesso!');
        onOpenChange(false);
      } else {
        toast.error(r.erro ?? 'Falha ao enviar.');
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const podeEnviar =
    numero.trim().length >= 10 &&
    ((modo === 'texto' && mensagem.trim().length > 0) ||
     (modo === 'template' && templateChave));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Enviar mensagem WhatsApp
          </DialogTitle>
          <DialogDescription>
            Envio direto via Z-API. O destinatário recebe no WhatsApp.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1">
            <Label>Número (com DDD) *</Label>
            <Input
              value={numero}
              onChange={(e) => setNumero(e.target.value)}
              placeholder="(11) 99999-9999"
            />
          </div>

          <Tabs value={modo} onValueChange={(v) => setModo(v as 'texto' | 'template')}>
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="texto">Texto livre</TabsTrigger>
              <TabsTrigger value="template">Usar template</TabsTrigger>
            </TabsList>

            <TabsContent value="texto" className="pt-3">
              <Label>Mensagem *</Label>
              <Textarea
                value={mensagem}
                onChange={(e) => setMensagem(e.target.value)}
                rows={5}
                placeholder="Digite a mensagem..."
              />
            </TabsContent>

            <TabsContent value="template" className="pt-3 space-y-3">
              <div className="space-y-1">
                <Label>Template</Label>
                <Select value={templateChave} onValueChange={setTemplateChave}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um template..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(templates.query.data ?? []).filter(t => t.isAtivo).map(t => (
                      <SelectItem key={t.id} value={t.chave}>{t.titulo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {placeholdersTemplate.length > 0 && (
                <div className="space-y-2 border rounded p-3 bg-muted/30">
                  <Label className="text-xs text-muted-foreground">Variáveis do template</Label>
                  {placeholdersTemplate.map(key => (
                    <div key={key} className="flex items-center gap-2">
                      <span className="font-mono text-xs w-24 text-muted-foreground">{`{${key}}`}</span>
                      <Input
                        value={variaveis[key] ?? ''}
                        onChange={(e) => setVariaveis(v => ({ ...v, [key]: e.target.value }))}
                        placeholder={`valor de ${key}`}
                        className="flex-1"
                      />
                    </div>
                  ))}
                </div>
              )}

              {templateSelecionado && (
                <div className="border rounded p-3 bg-green-50 dark:bg-green-950/20 text-sm whitespace-pre-wrap">
                  <Label className="text-xs text-muted-foreground mb-1 block">Preview</Label>
                  {preview}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={handleEnviar}
            disabled={!podeEnviar || enviar.isPending}
            className="gap-2"
          >
            <Send className="h-4 w-4" />
            {enviar.isPending ? 'Enviando...' : 'Enviar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
